import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import {
  apiErrorMessage,
  integrationsService,
  inventoryService,
  suppliersService,
  taxTypesService,
} from '../../services/api';
import { EmptyState, PageHeader, TableSkeleton } from '../../components/ui';
import { useShop } from '../../contexts/ShopContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCachedData } from '../../hooks/useCachedData';
import {
  INTEGRATION_KEYS,
  Part,
  PartPayload,
  PriceImportResult,
  StockMovement,
  SupplierStatus,
  TaxType,
  UserRole,
} from '../../types';
import { APP_LOCALE, formatTimestamp } from '../../utils/locale';

/**
 * The shop's parts and stock.
 *
 * Stock never changes by editing a number: it moves through the ledger —
 * receipts from purchase orders, parts fitted on issued invoices, imports and
 * the adjustments made here, each with a reason.
 */

const PAGE_SIZE = 50;

const num = (value: number | string | null | undefined) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (value: number | string | null | undefined) =>
  num(value).toLocaleString(APP_LOCALE, { style: 'currency', currency: 'USD' });
const qty = (value: number | string | null | undefined) =>
  num(value).toLocaleString(APP_LOCALE, { maximumFractionDigits: 2 });

const MOVEMENT_LABELS: Record<StockMovement['type'], string> = {
  receive: 'Received',
  consume: 'Fitted',
  adjust: 'Adjusted',
  return: 'Returned',
  import: 'Imported',
};

interface PartForm {
  partNumber: string;
  brand: string;
  name: string;
  sku: string;
  unitCost: string;
  unitPrice: string;
  coreCharge: string;
  taxTypeId: string;
  reorderPoint: string;
  reorderQuantity: string;
  binLocation: string;
  preferredSupplier: string;
  openingStock: string;
  isActive: boolean;
}

const EMPTY_FORM: PartForm = {
  partNumber: '',
  brand: '',
  name: '',
  sku: '',
  unitCost: '0.00',
  unitPrice: '0.00',
  coreCharge: '0.00',
  taxTypeId: '',
  reorderPoint: '0',
  reorderQuantity: '0',
  binLocation: '',
  preferredSupplier: '',
  openingStock: '0',
  isActive: true,
};

const isLow = (part: Part) =>
  num(part.reorderPoint) > 0 && num(part.onHand) <= num(part.reorderPoint);

const NO_PARTS: Part[] = [];
const NO_TAX_TYPES: TaxType[] = [];

const InventoryPage: React.FC = () => {
  const { selectedShop } = useShop();
  const { user } = useAuth();
  const shopId = user?.role === UserRole.SUPER_ADMIN ? selectedShop?.id : undefined;

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [lowStock, setLowStock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [suppliers, setSuppliers] = useState<SupplierStatus[]>([]);
  const [importAllowed, setImportAllowed] = useState(true);

  const [editing, setEditing] = useState<Part | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<PartForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [adjusting, setAdjusting] = useState<Part | null>(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  const [historyFor, setHistoryFor] = useState<Part | null>(null);
  const [history, setHistory] = useState<StockMovement[]>([]);

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importStock, setImportStock] = useState(true);
  const [importMarkup, setImportMarkup] = useState('');
  const [importSupplier, setImportSupplier] = useState('');
  const [importResult, setImportResult] = useState<PriceImportResult | null>(null);

  // Debounce the search box so typing is not a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // From the session cache first, then reloaded: returning here paints at once.
  const partsData = useCachedData(`parts:${shopId ?? ''}:${query}:${lowStock}:${page}`, () =>
    inventoryService.listParts({
      search: query || undefined,
      lowStock: lowStock || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      shopId,
    }),
  );
  const parts = partsData.data?.rows ?? NO_PARTS;
  const loading = partsData.loading;
  const load = partsData.reload;
  // The pager keeps the last known total while another page loads.
  const lastTotal = useRef(0);
  if (partsData.data) lastTotal.current = partsData.data.total;
  const total = partsData.data?.total ?? lastTotal.current;

  useEffect(() => {
    setError(partsData.error ? apiErrorMessage(partsData.error, 'Could not load inventory.') : null);
  }, [partsData.error]);

  const taxTypes = useCachedData('tax-types', () => taxTypesService.getTaxTypes()).data ?? NO_TAX_TYPES;

  useEffect(() => {
    suppliersService
      .list(shopId)
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
    integrationsService
      .getAccess(shopId)
      .then((access) =>
        setImportAllowed(
          access.find((entry) => entry.key === INTEGRATION_KEYS.PRICE_IMPORT)?.enabled ?? true,
        ),
      )
      .catch(() => setImportAllowed(true));
  }, [shopId]);

  const field =
    (key: keyof PartForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (part: Part) => {
    setEditing(part);
    setForm({
      partNumber: part.partNumber,
      brand: part.brand ?? '',
      name: part.name,
      sku: part.sku,
      unitCost: num(part.unitCost).toFixed(2),
      unitPrice: num(part.unitPrice).toFixed(2),
      coreCharge: num(part.coreCharge).toFixed(2),
      taxTypeId: part.taxTypeId ?? '',
      reorderPoint: String(num(part.reorderPoint)),
      reorderQuantity: String(num(part.reorderQuantity)),
      binLocation: part.binLocation ?? '',
      preferredSupplier: part.preferredSupplier ?? '',
      openingStock: '0',
      isActive: part.isActive,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload: PartPayload = {
      partNumber: form.partNumber.trim(),
      brand: form.brand.trim() || undefined,
      name: form.name.trim(),
      sku: form.sku.trim() || undefined,
      unitCost: num(form.unitCost),
      unitPrice: num(form.unitPrice),
      coreCharge: num(form.coreCharge),
      taxTypeId: form.taxTypeId || undefined,
      reorderPoint: num(form.reorderPoint),
      reorderQuantity: num(form.reorderQuantity),
      binLocation: form.binLocation.trim() || undefined,
      preferredSupplier: form.preferredSupplier || undefined,
    };
    try {
      if (editing) {
        await inventoryService.updatePart(editing.id, { ...payload, isActive: form.isActive });
        setNotice(`${payload.name} updated`);
      } else {
        await inventoryService.createPart(
          { ...payload, openingStock: num(form.openingStock) || undefined },
          shopId,
        );
        setNotice(`${payload.name} added`);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the part.'));
    } finally {
      setSaving(false);
    }
  };

  const handleAdjust = async () => {
    if (!adjusting) return;
    setSaving(true);
    try {
      await inventoryService.adjustStock(adjusting.id, {
        quantity: num(adjustQty),
        note: adjustNote.trim(),
      });
      setNotice('Stock adjusted');
      setAdjusting(null);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not adjust stock.'));
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (part: Part) => {
    setHistoryFor(part);
    setHistory([]);
    try {
      setHistory(await inventoryService.movements(part.id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load stock history.'));
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setSaving(true);
    setError(null);
    try {
      const result = await inventoryService.importPriceFile(
        importFile,
        {
          updateStock: importStock,
          supplier: importSupplier || undefined,
          markupPercent: importMarkup.trim() ? num(importMarkup) : undefined,
        },
        shopId,
      );
      setImportResult(result);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not import the price file.'));
    } finally {
      setSaving(false);
    }
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <PageHeader
        eyebrow={selectedShop?.name}
        title="Inventory"
        subtitle="Parts on your shelves. Stock moves when orders are received, when an issued invoice fits a part, and when you adjust it here — every change is kept in the part's history."
        actions={
          <Stack direction="row" spacing={1}>
            {importAllowed && (
              <Button startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
                Import price file
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New part
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search SKU, part number, name or brand"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: 1, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={lowStock}
              onChange={(event) => {
                setLowStock(event.target.checked);
                setPage(0);
              }}
            />
          }
          label="Only low stock"
        />
      </Stack>

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {!loading && parts.length === 0 ? (
          <EmptyState
            icon={<InventoryIcon />}
            title={query || lowStock ? 'No parts match' : 'No parts yet'}
            description={
              query || lowStock
                ? 'Try another search, or clear the low-stock filter.'
                : 'Add parts by hand, import a supplier price file, or receive a purchase order — received parts are added automatically.'
            }
            action={
              query || lowStock ? undefined : (
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  New part
                </Button>
              )
            }
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Part</TableCell>
                  <TableCell>Bin</TableCell>
                  <TableCell align="right">On hand</TableCell>
                  <TableCell align="right">Cost</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton rows={6} columns={6} />
                ) : (
                  parts.map((part) => (
                    <TableRow key={part.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontWeight={600}>{part.name}</Typography>
                          {!part.isActive && <Chip size="small" variant="outlined" label="Retired" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {part.sku}
                          {part.brand ? ` · ${part.brand} ${part.partNumber}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{part.binLocation || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          {isLow(part) && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`Reorder at ${qty(part.reorderPoint)}`}
                            />
                          )}
                          <Typography
                            fontWeight={600}
                            color={num(part.onHand) < 0 ? 'error' : undefined}
                          >
                            {qty(part.onHand)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(part.unitCost)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(part.unitPrice)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Adjust stock">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setAdjusting(part);
                                setAdjustQty('');
                                setAdjustNote('');
                              }}
                            >
                              <TuneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Stock history">
                            <IconButton size="small" onClick={() => openHistory(part)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(part)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {total > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_, next) => setPage(next)}
          />
        )}
      </Paper>

      {/* Create / edit */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'New part'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required value={form.name} onChange={field('name')} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Part number"
                required
                fullWidth
                value={form.partNumber}
                onChange={field('partNumber')}
              />
              <TextField label="Brand" fullWidth value={form.brand} onChange={field('brand')} />
            </Stack>
            <TextField
              label="SKU"
              value={form.sku}
              onChange={field('sku')}
              helperText="Leave blank to use BRAND-PARTNUMBER."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Cost"
                type="number"
                fullWidth
                value={form.unitCost}
                onChange={field('unitCost')}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
                label="Price"
                type="number"
                fullWidth
                value={form.unitPrice}
                onChange={field('unitPrice')}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <TextField
                label="Core charge"
                type="number"
                fullWidth
                value={form.coreCharge}
                onChange={field('coreCharge')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Stack>
            <TextField select label="Tax type" value={form.taxTypeId} onChange={field('taxTypeId')}>
              <MenuItem value="">Shop default</MenuItem>
              {taxTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Reorder at"
                type="number"
                fullWidth
                value={form.reorderPoint}
                onChange={field('reorderPoint')}
                inputProps={{ min: 0, step: '1' }}
                helperText="0 turns alerts off."
              />
              <TextField
                label="Reorder quantity"
                type="number"
                fullWidth
                value={form.reorderQuantity}
                onChange={field('reorderQuantity')}
                inputProps={{ min: 0, step: '1' }}
              />
              <TextField label="Bin" fullWidth value={form.binLocation} onChange={field('binLocation')} />
            </Stack>
            <TextField
              select
              label="Preferred supplier"
              value={form.preferredSupplier}
              onChange={field('preferredSupplier')}
            >
              <MenuItem value="">None</MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.provider} value={supplier.provider}>
                  {supplier.displayName}
                </MenuItem>
              ))}
            </TextField>
            {editing ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, isActive: event.target.checked }))
                    }
                  />
                }
                label="Active (retire parts you no longer stock)"
              />
            ) : (
              <TextField
                label="Opening stock"
                type="number"
                value={form.openingStock}
                onChange={field('openingStock')}
                inputProps={{ min: 0, step: '1' }}
                helperText="Recorded in the history as an opening balance."
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.partNumber.trim()}
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add part'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adjust */}
      <Dialog open={Boolean(adjusting)} onClose={() => setAdjusting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust {adjusting?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              On hand now: {qty(adjusting?.onHand)}. Use a negative number to remove stock.
            </Typography>
            <TextField
              label="Change"
              type="number"
              value={adjustQty}
              onChange={(event) => setAdjustQty(event.target.value)}
              inputProps={{ step: '1' }}
              autoFocus
            />
            <TextField
              label="Reason"
              required
              value={adjustNote}
              onChange={(event) => setAdjustNote(event.target.value)}
              placeholder="Stocktake, damaged, found in back room…"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAdjusting(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdjust}
            disabled={saving || num(adjustQty) === 0 || !adjustNote.trim()}
          >
            Adjust
          </Button>
        </DialogActions>
      </Dialog>

      {/* History */}
      <Dialog open={Boolean(historyFor)} onClose={() => setHistoryFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Stock history — {historyFor?.name}</DialogTitle>
        <DialogContent>
          {history.length === 0 ? (
            <Typography color="text.secondary">No stock movements yet.</Typography>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>What</TableCell>
                    <TableCell align="right">Change</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((movement) => (
                    <TableRow key={movement.id}>
                      <TableCell>{formatTimestamp(num(movement.occurredAt))}</TableCell>
                      <TableCell>{MOVEMENT_LABELS[movement.type]}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {num(movement.quantity) > 0 ? '+' : ''}
                        {qty(movement.quantity)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {qty(movement.balanceAfter)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {movement.note || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={() => setHistoryFor(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import */}
      <Dialog open={importOpen} onClose={closeImport} maxWidth="sm" fullWidth>
        <DialogTitle>Import a supplier price file</DialogTitle>
        <DialogContent>
          {importResult ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Alert severity="success">
                {importResult.created} added, {importResult.updated} updated,{' '}
                {importResult.unchanged} unchanged
                {importStock ? `, stock set on ${importResult.stockAdjusted}` : ''}.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Columns used:{' '}
                {Object.entries(importResult.columns)
                  .map(([key, column]) => `${column} → ${key}`)
                  .join(', ')}
              </Typography>
              {importResult.skipped.length > 0 && (
                <Alert severity="warning">
                  Skipped {importResult.skipped.length} row(s):{' '}
                  {importResult.skipped
                    .slice(0, 5)
                    .map((row) => `line ${row.line} (${row.reason})`)
                    .join('; ')}
                  {importResult.skipped.length > 5 ? '…' : ''}
                </Alert>
              )}
            </Stack>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                A CSV with a header row. Recognized columns include Part Number (or Part #, MPN),
                Brand (or Line Code), Description, Cost, List, Core and Qty (or QOH). Re-importing
                next month's file updates costs rather than duplicating parts.
              </Typography>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                {importFile ? importFile.name : 'Choose CSV file'}
                <input
                  hidden
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => setImportFile(event.target.files?.[0] ?? null)}
                />
              </Button>
              <FormControlLabel
                control={
                  <Switch checked={importStock} onChange={(event) => setImportStock(event.target.checked)} />
                }
                label="Set on-hand stock from the file's quantity column"
              />
              <TextField
                label="Markup % when the file has no list price"
                type="number"
                value={importMarkup}
                onChange={(event) => setImportMarkup(event.target.value)}
                inputProps={{ min: 0, step: '1' }}
              />
              <TextField
                select
                label="Supplier"
                value={importSupplier}
                onChange={(event) => setImportSupplier(event.target.value)}
                helperText="Recorded as the preferred supplier on new parts."
              >
                <MenuItem value="">None</MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.provider} value={supplier.provider}>
                    {supplier.displayName}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeImport}>{importResult ? 'Done' : 'Cancel'}</Button>
          {!importResult && (
            <Button variant="contained" onClick={handleImport} disabled={saving || !importFile}>
              {saving ? 'Importing…' : 'Import'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ height: 24 }} />
    </Container>
  );
};

export default InventoryPage;
